import { useEffect } from 'react';
import { trackError, trackWarning } from '../utils/errorTracking';

export const useErrorHandler = () => {
  useEffect(() => {
    // Guard flags to avoid recursive loops when we track within console overrides
    let isTrackingError = false;
    let isTrackingWarn = false;

    const safeSerialize = (value) => {
      try {
        if (value == null) return String(value);
        if (typeof value === 'string') return value.length > 1000 ? value.slice(0, 1000) + '…' : value;
        if (typeof value === 'number' || typeof value === 'boolean') return String(value);
        // Attempt JSON.stringify with cycle protection and size cap
        const seen = new WeakSet();
        const json = JSON.stringify(value, (key, val) => {
          if (typeof val === 'object' && val !== null) {
            if (seen.has(val)) return '[Circular]';
            seen.add(val);
          }
          return val;
        });
        if (!json) return Object.prototype.toString.call(value);
        return json.length > 2000 ? json.slice(0, 2000) + '…' : json;
      } catch (e) {
        return Object.prototype.toString.call(value);
      }
    };

    // Capturar errores no manejados de JavaScript
    const handleUnhandledError = (event) => {
      const error = event.error || event.reason || new Error('Unknown error');
      
      const errorContext = {
        type: 'unhandled_error',
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        errorMessage: error.message,
        errorName: error.name,
        errorStack: error.stack,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      };

      trackError(error, errorContext);
      
      // Prevenir que el error se muestre en la consola del navegador
      event.preventDefault();
    };

    // Capturar promesas rechazadas no manejadas
    const handleUnhandledRejection = (event) => {
      const error = event.reason || new Error('Unhandled promise rejection');
      
      const errorContext = {
        type: 'unhandled_promise_rejection',
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        errorMessage: error.message,
        errorName: error.name,
        errorStack: error.stack
      };

      trackError(error, errorContext);
      
      // Prevenir que el error se muestre en la consola del navegador
      event.preventDefault();
    };

    // Capturar errores de recursos (imágenes, scripts, etc.)
    const handleResourceError = (event) => {
      const error = new Error(`Resource error: ${event.target.src || event.target.href}`);
      
      const errorContext = {
        type: 'resource_error',
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        resourceType: event.target.tagName,
        resourceSrc: event.target.src || event.target.href,
        errorMessage: error.message
      };

      trackWarning(error.message, errorContext);
    };

    // Capturar errores de consola
    const originalConsoleError = console.error;
    const originalConsoleWarn = console.warn;
    
    console.error = (...args) => {
      // Llamar al método original
      originalConsoleError.apply(console, args);
      
      // Log en GA4 si es un error importante
      const errorMessage = args.map(arg => safeSerialize(arg)).join(' ');
      
      if (!isTrackingError && (errorMessage.includes('Error') || errorMessage.includes('Exception'))) {
        isTrackingError = true;
        const error = new Error(errorMessage);
        trackError(error, {
          type: 'console_error',
          userAgent: navigator.userAgent,
          url: window.location.href,
          timestamp: new Date().toISOString()
        });
        isTrackingError = false;
      }
    };

    console.warn = (...args) => {
      // Llamar al método original
      originalConsoleWarn.apply(console, args);
      
      // Log en GA4 para warnings importantes
      const warningMessage = args.map(arg => safeSerialize(arg)).join(' ');
      
      if (!isTrackingWarn) {
        isTrackingWarn = true;
        trackWarning(warningMessage, {
          type: 'console_warning',
          userAgent: navigator.userAgent,
          url: window.location.href,
          timestamp: new Date().toISOString()
        });
        isTrackingWarn = false;
      }
    };

    // Agregar event listeners
    window.addEventListener('error', handleUnhandledError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleResourceError, true);

    // Cleanup function
    return () => {
      window.removeEventListener('error', handleUnhandledError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleResourceError, true);
      
      // Restaurar métodos originales de console
      console.error = originalConsoleError;
      console.warn = originalConsoleWarn;
    };
  }, []);
};

export default useErrorHandler;
